import { useEffect, useState } from 'react';

interface HeroSectionProps {
  onOpenForm: () => void;
}

export default function HeroSection({ onOpenForm }: HeroSectionProps) {
  const [leadsCount, setLeadsCount] = useState(0);

  useEffect(() => {
    const target = 127400;
    const duration = 1800;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setLeadsCount(target);
        clearInterval(timer);
      } else {
        setLeadsCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#060910]">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/af17aacab537ee612322104a4f251e49.jpg"
          alt=""
          className="w-full h-full object-cover object-center opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#060910] via-[#060910]/85 to-[#060910]/55"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#060910] via-transparent to-[#060910]/30"></div>
      </div>

      {/* Accent glow */}
      <div className="absolute left-1/3 top-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-emerald-500/4 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-center">

          {/* LEFT */}
          <div>
            {/* Top badges */}
            <div className="flex flex-wrap gap-2 mb-7">
              <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-4 py-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                <span className="text-emerald-300 text-xs font-bold tracking-widest uppercase">New Website Included with package</span>
              </div>

            </div>

            {/* Headline — outcome-driven */}
            <h1 className="text-4xl md:text-5xl lg:text-[3.85rem] font-black text-white leading-[1.04] tracking-tight mb-5">
              Your Website Should Be{' '}
              <span className="relative">
                <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-300 bg-clip-text text-transparent">
                  Generating Leads
                </span>
              </span>{' '}
              Right Now
            </h1>

            {/* Subheadline — offer clearly stated */}
            <p className="text-white/75 text-lg md:text-xl leading-relaxed mb-3 max-w-xl">
              We rebuild your website from scratch and run your full SEO system — optimized for AI search, voice, Google & Maps — for{' '}
              <strong className="text-white font-bold">$399/month with $0 extra upfront for website build.</strong>
            </p>
            <p className="text-white/38 text-sm mb-9 max-w-lg">
              Most websites are built for 2015. Yours will be engineered for how customers actually search in 2026.
            </p>

            {/* Dual CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-9">
              <button
                onClick={onOpenForm}
                className="whitespace-nowrap bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-base px-8 py-4 rounded-full hover:opacity-90 hover:scale-[1.02] transition-all duration-200 cursor-pointer flex items-center justify-center gap-2.5 shadow-lg shadow-emerald-900/50"
              >
                <i className="ri-eye-line" style={{ fontSize: '18px' }}></i>
                Get Your Free Website Preview
              </button>
              <a
                href="tel:8004559670"
                className="whitespace-nowrap flex items-center justify-center gap-2.5 bg-white/7 border border-white/18 text-white font-semibold text-base px-7 py-4 rounded-full hover:bg-white/12 transition-all duration-200 cursor-pointer"
              >
                <i className="ri-phone-fill text-emerald-400" style={{ fontSize: '18px' }}></i>
                Call 800-455-9670
              </a>
            </div>

            {/* Trust row */}
            <div className="grid grid-cols-3 gap-4 pb-8 border-b border-white/7 mb-7">
              {[
                { val: '500+', label: 'USA Businesses' },
                { val: '4.9★', label: 'Client Rating' },
                { val: '12yrs', label: 'In Business' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-white text-xl md:text-2xl font-black leading-none">{s.val}</p>
                  <p className="text-white/30 text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Platform tags */}
            <div>
              <p className="text-white/28 text-xs uppercase tracking-widest font-semibold mb-3">Optimized for every search platform</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: 'ri-robot-2-line', label: 'AI Search' },
                  { icon: 'ri-mic-line', label: 'Voice Search' },
                  { icon: 'ri-global-line', label: 'Google' },
                  { icon: 'ri-map-pin-2-line', label: 'Maps & Local' },
                ].map((tag) => (
                  <div key={tag.label} className="flex items-center gap-1.5 bg-white/5 border border-white/9 rounded-lg px-3 py-1.5 hover:border-emerald-500/25 hover:bg-emerald-500/5 transition-colors duration-200 cursor-default">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${tag.icon} text-emerald-400`} style={{ fontSize: '13px' }}></i>
                    </span>
                    <span className="text-white/50 text-xs font-medium">{tag.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Visual */}
          <div className="relative hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-[560px]">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/12 to-teal-500/8 rounded-3xl blur-3xl scale-110 pointer-events-none"></div>

              {/* Main mockup */}
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=premium%20sleek%20modern%20business%20website%20displayed%20on%20MacBook%20Pro%20laptop%20screen%20ultra-clean%20minimal%20white%20and%20dark%20UI%20professional%20homepage%20hero%20section%20sharp%20typography%20beautiful%20web%20design%202026%20clean%20layout%20device%20mockup%20dark%20studio%20backdrop&width=900&height=620&seq=hero-mockup-v4-99mkt&orientation=landscape"
                  alt="Premium modern website mockup"
                  className="w-full object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#060910]/70 to-transparent"></div>
              </div>

              {/* Floating stat cards */}
              <div className="absolute -top-5 -left-7 bg-[#0d1a18] border border-emerald-500/30 rounded-xl px-4 py-3 backdrop-blur-sm shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-500/20">
                    <i className="ri-line-chart-line text-emerald-400" style={{ fontSize: '16px' }}></i>
                  </div>
                  <div>
                    <p className="text-emerald-300 text-xs font-black">
                      {leadsCount.toLocaleString()}+
                    </p>
                    <p className="text-white/35 text-xs">leads generated</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-5 -right-5 bg-[#0d1a18] border border-white/12 rounded-xl px-4 py-3 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-teal-500/20">
                    <i className="ri-speed-up-line text-teal-300" style={{ fontSize: '16px' }}></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-black">&lt;2s Load Time</p>
                    <p className="text-white/35 text-xs">Performance ready</p>
                  </div>
                </div>
              </div>

              <div className="absolute top-1/2 -right-7 -translate-y-1/2 bg-[#0d1a18] border border-white/10 rounded-xl px-4 py-4 shadow-xl">
                <p className="text-white/30 text-xs font-semibold uppercase tracking-widest mb-2.5">Found on</p>
                <div className="space-y-2">
                  {[
                    { icon: 'ri-robot-2-line', label: 'AI Tools' },
                    { icon: 'ri-mic-line', label: 'Voice' },
                    { icon: 'ri-google-line', label: 'Google' },
                    { icon: 'ri-map-pin-2-line', label: 'Maps' },
                  ].map((t) => (
                    <div key={t.label} className="flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${t.icon} text-emerald-400`} style={{ fontSize: '12px' }}></i>
                      </span>
                      <span className="text-white/50 text-xs">{t.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview ready badge */}
              <div className="absolute top-4 right-4 bg-emerald-500/20 border border-emerald-500/35 rounded-full px-3 py-1.5 backdrop-blur-sm">
                <p className="text-emerald-300 text-xs font-bold">Preview in 48hrs</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom fade to white */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
    </section>
  );
}
